import React, { FC, useState, useMemo, useRef, useReducer } from 'react';
import { getBrandingColors } from 'src/brand-generation/colors';
import { HeadingTypeface, useApplyBrandColors } from 'src/branding/set-brand-colors';
import { Checkbox } from 'src/common/form/checkbox';
import { RadioGroup } from 'src/common/form/radio-group';
import { Organization } from 'src/common/interfaces/organization.interface';
import { OrganizationBranding } from 'src/common/interfaces/organization/branding.interface';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { CalendarStrongIcon } from 'src/monet/icons';
import { useCurrentOrganization } from 'src/store/organization';
import { apiUpdateOrganizationBranding } from 'src/utils/journeyApi';
import { DashboardSettingsThemeOptionLabelContainer } from './option-label-container';
import { useCurrentSubscription } from 'src/store/subscription';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';

import journeyLogoVector from 'src/icons/journey-logo-vector.svg';
import SignupBlockPreviewImage from 'src/images/signup-theme-bg.png';

import ThemeThumbnailImage from './theme-thumbnail-image.png';
import OrganizationLink from 'src/common/OrganizationLink';
import { trackSegmentEvent } from 'src/utils/segment';
import classNames from 'classnames';

type DashboardSettingsThemeProps = {
  currentOrganization: Organization;
};

export type ThumbnailStyle = 'image' | 'icon' | '';


export const DashboardSettingsTheme: FC<DashboardSettingsThemeProps> = ({ currentOrganization }) => {
  const { setCurrentOrganization } = useCurrentOrganization();
  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();
  const { applyBrandColors } = useApplyBrandColors();
  const { isPro, isEnterprise, isOnTrial } = useCurrentSubscription();
  const { features } = useFeatureRestrictions();

  const [submitting, setSubmitting] = useState(false);
  const headingTypeface = (currentOrganization.branding?.heading_typeface || '') as HeadingTypeface;
  const [draftHeadingTypeface, setDraftHeadingTypeface] = useState<HeadingTypeface>(headingTypeface);
  const thumbnailStyle = (currentOrganization.branding?.thumbnail_style || '') as ThumbnailStyle;
  const [draftThumbnailStyle, setDraftThumbnailStyle] = useState<ThumbnailStyle>(thumbnailStyle);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const [hideJourneyUpsell, setHideJourneyUpsell] = useState(
    currentOrganization.branding?.hide_journey_upsell || false
  );
  const [hideSignupBlock, setHideSignupBlock] = useState(currentOrganization.branding?.hide_signup_block || false);

  const initialValuesRef = useRef({
    draftHeadingTypeface: headingTypeface,
    draftThumbnailStyle: thumbnailStyle,
    hideJourneyUpsell,
    hideSignupBlock,
  });

  const onSubmit = async () => {
    setSubmitting(true);

    let params: Record<string, any> = {};
    if (draftHeadingTypeface) {
      params.heading_typeface = draftHeadingTypeface;
    }
    if (draftThumbnailStyle) {
      params.thumbnail_style = draftThumbnailStyle;
    }

    params.hide_journey_upsell = hideJourneyUpsell;
    params.hide_signup_block = hideSignupBlock;

    try {
      const organization = await apiUpdateOrganizationBranding(currentOrganization.id, params);
      initialValuesRef.current = { draftHeadingTypeface, draftThumbnailStyle, hideJourneyUpsell, hideSignupBlock };
      setSubmitting(false);
      setCurrentOrganization(organization);

      const brandingBaseColor = organization.branding?.secondary_color;
      const { brandPrimaryColor, brandSecondaryColor, brandTextColor } = getBrandingColors(
        brandingBaseColor,
        organization.branding?.force_generated_colors || false
      );

      applyBrandColors(brandPrimaryColor, brandSecondaryColor, brandTextColor, organization.branding?.heading_typeface);
      setSuccessNotification('Theme updated successfully');
      logThemeChangeToAnalytics(organization.branding);
      forceUpdate();
    } catch (e: any) {
      setSubmitting(false);
      setErrorNotification(e.message);
    }
  };

  const logThemeChangeToAnalytics = (branding: OrganizationBranding) => {
    trackSegmentEvent('Theme Settings Updated', {
      Typography: branding.heading_typeface,
      Thumbnails: branding.thumbnail_style,
      'Made in Journey': branding.hide_journey_upsell ? false : true,
      'Sign up block': branding.hide_signup_block ? false : true,
    });
  };

  const isFormDisabled = useMemo(() => {
    return (
      submitting ||
      (initialValuesRef.current.draftHeadingTypeface === draftHeadingTypeface &&
        initialValuesRef.current.draftThumbnailStyle === draftThumbnailStyle &&
        initialValuesRef.current.hideJourneyUpsell === hideJourneyUpsell &&
        initialValuesRef.current.hideSignupBlock === hideSignupBlock)
    );
  }, [draftHeadingTypeface, draftThumbnailStyle, hideJourneyUpsell, hideSignupBlock, submitting]);

  return (
    <div className='flex flex-row space-x-xl relative mt-md'>
      <div className='flex flex-col space-y-md'>
        <div className='flex flex-col space-y-lg mb-2'>
          <div className='flex flex-col space-y-md'>
            <div className='text-bedrock-p-strong text-bedrock-black'>Typography</div>
            <div className='flex flex-col'>
              <RadioGroup
                name='typography'
                itemClassName='flex flex-row-reverse items-center justify-between'
                className='!space-y-4'
                options={[
                  {
                    label: (
                      <DashboardSettingsThemeOptionLabelContainer
                        selected={(draftHeadingTypeface || headingTypeface) === 'serif'}
                      >
                        <div className='text-[26px] leading-[26px] font-brand-serif font-normal pl-6'>Nib Pro</div>
                      </DashboardSettingsThemeOptionLabelContainer>
                    ),
                    value: 'serif',
                  },
                  {
                    label: (
                      <DashboardSettingsThemeOptionLabelContainer
                        selected={(draftHeadingTypeface || headingTypeface) === 'sans_serif'}
                      >
                        <div className='text-[26px] leading-[26px] font-brand-sans-serif font-light pl-6'>Circular</div>
                      </DashboardSettingsThemeOptionLabelContainer>
                    ),
                    value: 'sans_serif',
                  },
                ]}
                value={draftHeadingTypeface}
                onChange={(option) => {
                  setDraftHeadingTypeface(option.value as HeadingTypeface);
                }}
              />
            </div>
          </div>
          <div className='flex flex-col space-y-md'>
            <div className='text-bedrock-p-strong text-bedrock-black'>Thumbnails</div>
            <div className='flex flex-col'>
              <RadioGroup
                name='thumbnails'
                itemClassName='flex flex-row-reverse items-center justify-between'
                className='!space-y-4'
                options={[
                  {
                    label: (
                      <DashboardSettingsThemeOptionLabelContainer
                        selected={(draftThumbnailStyle || thumbnailStyle) === 'image'}
                      >
                        <div className='flex flex-grow p-3'>
                          <div className='flex flex-grow space-x-2 items-center rounded-lg bg-white p-1'>
                            <img src={ThemeThumbnailImage} alt='Theme thumbnail' className='w-10 h-10 rounded-md' />
                            <div className='text-bedrock-p-small text-bedrock-black'>Book time in calendar</div>
                          </div>
                        </div>
                      </DashboardSettingsThemeOptionLabelContainer>
                    ),
                    value: 'image',
                  },
                  {
                    label: (
                      <DashboardSettingsThemeOptionLabelContainer
                        selected={(draftThumbnailStyle || thumbnailStyle) === 'icon'}
                      >
                        <div className='flex flex-grow p-3'>
                          <div className='flex flex-grow space-x-2 items-center rounded-lg bg-white p-1'>
                            <div className='flex flex-shrink-0 h-10 w-10 bg-journey-primary-blue items-center justify-center min-w-0 relative rounded-lg text-white'>
                              <CalendarStrongIcon />
                            </div>
                            <div className='text-bedrock-p-small text-bedrock-black'>Book time in calendar</div>
                          </div>
                        </div>
                      </DashboardSettingsThemeOptionLabelContainer>
                    ),
                    value: 'icon',
                  },
                ]}
                value={draftThumbnailStyle}
                onChange={(option) => {
                  setDraftThumbnailStyle(option.value as ThumbnailStyle);
                }}
              />
            </div>
          </div>
          <div className='flex flex-col space-y-md'>
            <div className='text-bedrock-p-strong text-bedrock-black'>Made with Journey, signup block</div>

            <div
              className={classNames('flex items-center justify-between', {
                'hover:opacity-80': features.canRemoveBranding,
              })}
            >
              <label
                htmlFor='theme-hide-journey-upsell'
                className={classNames({
                  'cursor-pointer': features.canRemoveBranding,
                  'pointer-events-none': !features.canRemoveBranding,
                })}
              >
                <DashboardSettingsThemeOptionLabelContainer selected={false}>
                  <div className='flex flex-grow p-3'>
                    <div className='flex flex-grow space-x-2 items-center rounded-lg bg-white p-1'>
                      <div className='w-10 h-10 rounded-md bg-journey-primary-blue flex items-center justify-center'>
                        <img src={journeyLogoVector} alt='Made with Journey' />
                      </div>
                      <div className='text-bedrock-p-small text-journey-primary-blue'>Made with Journey</div>
                    </div>
                  </div>
                </DashboardSettingsThemeOptionLabelContainer>
              </label>
              <Checkbox
                id='theme-hide-journey-upsell'
                className='flex flex-row-reverse items-center justify-between'
                disabled={!features.canRemoveBranding}
                checked={!hideJourneyUpsell}
                onChange={(e, checked) => {
                  if (!features.canRemoveBranding) {
                    return;
                  }
                  setHideJourneyUpsell(!checked);
                }}
                isBedrock
              />
            </div>
            <div
              className={classNames('flex items-center justify-between', {
                'hover:opacity-80': features.canRemoveBranding,
              })}
            >
              <label
                htmlFor='theme-hide-signup-block'
                className={classNames({
                  'cursor-pointer': features.canRemoveBranding,
                  'pointer-events-none': !features.canRemoveBranding,
                })}
              >
                <DashboardSettingsThemeOptionLabelContainer className='overflow-hidden border-b-0' selected={false}>
                  <img src={SignupBlockPreviewImage} alt='' className='p-4 pb-0' />
                </DashboardSettingsThemeOptionLabelContainer>
              </label>
              <Checkbox
                id='theme-hide-signup-block'
                className='flex flex-row-reverse items-center justify-between'
                disabled={!features.canRemoveBranding}
                checked={!hideSignupBlock}
                onChange={(e, checked) => {
                  if (!features.canRemoveBranding) {
                    return;
                  }
                  setHideSignupBlock(!checked);
                }}
                isBedrock
              />
            </div>
          </div>
        </div>
        <div className='flex justify-start'>
          <button type='button' className='btn-bedrock-primary' onClick={onSubmit} disabled={isFormDisabled}>
            Save
          </button>
        </div>
      </div>

      <div className='mt-[38px] pl-lg h-[516px] border-l border-bedrock-gray-medium'>
        <div className='flex flex-col items-start max-w-[404px] mt-[25px]'>
          <div className='flex text-bedrock-p text-bedrock-black mb-2'>Pick the font style you would like to use</div>
          <div className='flex text-bedrock-p-small text-bedrock-black'>
            Nib Pro is a serif from the Colophon foundry.
          </div>
          <div className='flex text-bedrock-p-small text-bedrock-black'>
            Circular is a sans serif from Lineto foundry.
          </div>
        </div>
        <div className='flex flex-col items-start max-w-[404px] mt-[153px]'>
          <div className='flex text-bedrock-p text-bedrock-black'>
            Decide how you would like your Step thumbnails to look
          </div>
        </div>
        <div className='flex flex-col items-start max-w-[404px] mt-[192px]'>
          <div className='flex flex-col text-bedrock-p text-bedrock-black'>
            {features.canRemoveBranding ? (
              <span>
                Feeling philanthropic? Decide if you'd like to tell the world that you're using Journey via a small banner within your Journey.
              </span>
            ) : (
              <>
                <span className='mb-3'>
                  Upgrade to Grow or Scale plan to disable the Made with Journey watermark or signup block.
                </span>
                <a href='/billing' className='btn-bedrock-primary' style={{ maxWidth: '100px' }}>Upgrade</a>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
